import type { GlobeProject } from "./google-maps-globe-clustering";

export const VIETNAM_CENTER = { lat: 14.0583, lng: 108.2772 };
export const INITIAL_RANGE_METERS = 12_000_000;
export const TILESET_SHOW_MULTIPLIER = 3;
export const CLUSTER_PIXEL_RANGE = 60;
export const MINIMUM_CLUSTER_SIZE = 2;
export const MARKER_ICON_URL = "https://sanpham.starglobal3d.vn/map3d/images/icon-vn.gif";
export const MARKER_SIZE_PX = 50;

const clusterImageCache = new Map<number, string>();

export const buildClusterBillboardImage = (count: number): string => {
    const cached = clusterImageCache.get(count);
    if (cached) return cached;

    const size = 68;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
    ctx.fillStyle = "#008a00";
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 24px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(count), size / 2, size / 2 + 1);

    const dataUrl = canvas.toDataURL("image/png");
    clusterImageCache.set(count, dataUrl);
    return dataUrl;
};

export const getProjectTitle = (project: GlobeProject, locale: string): string => {
    if (locale === "en") return project.display_en || project.title || project.db_name || "";
    if (locale === "ja") return project.display_ja || project.display_en || project.title || project.db_name || "";
    return project.display_vn || project.display_en || project.title || project.db_name || "";
};

export type ValidProject = {
    project: GlobeProject;
    lat: number;
    lng: number;
};

export const filterValidProjects = (projects: GlobeProject[]): ValidProject[] => (
    projects
        .map((project) => ({ project, lat: Number(project.lat), lng: Number(project.lng) }))
        .filter((item): item is ValidProject => Number.isFinite(item.lat) && Number.isFinite(item.lng))
);

type EntityAdder = {
    entities: { add: (options: Record<string, unknown>) => unknown; removeAll: () => void };
};

type Cartesian3Factory = {
    Cartesian3: { fromDegrees: (lng: number, lat: number, height?: number) => unknown };
    Cartesian2: new (x: number, y: number) => unknown;
    VerticalOrigin: { BOTTOM: number; TOP: number };
    HorizontalOrigin: { CENTER: number };
    HeightReference: { RELATIVE_TO_GROUND: number };
    LabelStyle: { FILL_AND_OUTLINE: number };
    Color: { WHITE: unknown; BLACK: unknown };
};

export const rebuildProjectEntities = (
    Cesium: Cartesian3Factory,
    dataSource: EntityAdder,
    projects: GlobeProject[],
    locale: string,
): void => {
    dataSource.entities.removeAll();
    const validProjects = filterValidProjects(projects);
    validProjects.forEach(({ project, lat, lng }, index) => {
        const title = getProjectTitle(project, locale);
        dataSource.entities.add({
            id: `${project.url || project.title || project.db_name || "project"}-${index}`,
            position: Cesium.Cartesian3.fromDegrees(lng, lat, 50),
            billboard: {
                image: MARKER_ICON_URL,
                width: MARKER_SIZE_PX,
                height: MARKER_SIZE_PX,
                verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
            label: {
                text: title,
                font: "600 12px sans-serif",
                fillColor: Cesium.Color.WHITE,
                outlineColor: Cesium.Color.BLACK,
                outlineWidth: 3,
                style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                verticalOrigin: Cesium.VerticalOrigin.TOP,
                horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
                pixelOffset: new Cesium.Cartesian2(0, 6),
                heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
                disableDepthTestDistance: Number.POSITIVE_INFINITY,
            },
            properties: { project },
            name: title,
        });
    });
};
